import type { BaseFolderSecurity } from './group';
import type { GroupType, SortDirection } from './enums';
import {
  APP_TYPES,
  APP_REJECT_ASSIGN,
  APP_COMMON_TYPES_REJECT_DEVICE,
  APP_COMMON_TYPES_REJECT_SPLITSCREEN,
  BROADCAST_REJECT_APP_TYPES,
  COMMON_TYPE,
  PLAYLIST_IGNORE_APP_TYPES,
  WEB_TYPES,
} from './app-types';

// --- Enums ---

export const ASSET_TYPES = {
  FILE: 'file',
  WEB: 'web',
  APP: 'app',
  SPLIT_SCREEN: 'splitScreen',
  FOLDER: 'folder',
  DATASOURCE: 'datasource',
  TEMPLATE: 'template',
  // OptiEdge MDM Phase 1 — backs Room Signage URLs; excluded from standard asset queries.
  HIDDEN: 'hidden',
} as const;

export type AssetType = (typeof ASSET_TYPES)[keyof typeof ASSET_TYPES];

export const ROOMS_SIGNAGE_VENDORS = {
  GOOGLE_MEET: 'google-meet',
  TEAMS_ROOMS: 'teams-rooms',
  ZOOM_ROOMS: 'zoom-rooms',
  WEBEX_ROOMS: 'webex-rooms',
} as const;

export type RoomsSignageVendor = (typeof ROOMS_SIGNAGE_VENDORS)[keyof typeof ROOMS_SIGNAGE_VENDORS];

export interface RoomsSignageUrlMeta {
  kind: 'rooms-signage-url';
  vendor: RoomsSignageVendor;
  target?: string;
  assignedContent?: {
    type: 'asset' | 'playlist' | 'schedule';
    _id: string;
    name: string;
  };
}

export const FILE_TYPES = {
  IMAGE: 'image',
  VIDEO: 'video',
  AUDIO: 'audio',
  DOCUMENT: 'document',
  WEB: 'web',
} as const;

export type FileType = (typeof FILE_TYPES)[keyof typeof FILE_TYPES];

export const ASSET_STATUS = {
  ACTIVE: 'active',
  CONVERTING: 'converting',
  FINISHED: 'finished',
  FAILED: 'failed',
  /**
   * Reference stamp: set on playlist items / schedule items / split-screen zones whose
   * referenced object was moved to Trash. Cleared again on restore.
   */
  PENDING_DELETE: 'pending-delete',
} as const;

export type AssetStatus = (typeof ASSET_STATUS)[keyof typeof ASSET_STATUS];

// --- Value Objects ---

export interface AssetExpired {
  startDate?: string;
  endDate?: string;
  timezone?: string;
  scheduleId?: string;
  type?: string;
  is_delete?: boolean;
}

/** Input type for updateAssetExpires / deleteAssetExpires mutations */
export interface AssetExpiredInput {
  assetIds: string[];
  range?: {
    startDate?: string;
    endDate?: string;
  };
  timezone?: string;
  scheduleId?: string;
  type?: string;
  is_delete?: boolean;
}

export interface AssetOptions {
  [key: string]: unknown;
}

/**
 * A single selected report page on a Power BI multi-page container (`Asset.pages`).
 * Mirrors open-api's `AssetPage` GraphQL type. `duration` is resolved at READ time from the
 * container's hidden per-page child assets (task R1, `AssetService.resolvePowerBIPageDurations`)
 * — it is NOT persisted on the parent asset, so it can come back `null` (e.g. against an API
 * process that hasn't picked up that resolver yet). Consumers must tolerate a null/missing
 * `duration` rather than assuming it is always populated.
 */
export interface AssetPage {
  pageName?: string;
  displayName?: string;
  duration?: number | null;
}

/**
 * Minimal asset shape shared between Asset and PlaylistAsset.
 * Used by getAssetDef() and AssetThumbnail to avoid unsafe double casts.
 * Both Asset and PlaylistAsset structurally satisfy this interface.
 */
export interface AssetLike {
  _id?: string;
  type?: string;
  fileType?: string;
  appType?: string;
  webType?: string;
  subType?: string;
  commonType?: string;
  serviceType?: string;
  youtubeType?: string;
  status?: string;
  thumbnail?: string;
  bucket?: string;
  AWSS3ID?: string;
  originalFileName?: string;
  assetExpired?: AssetExpired;
  isHide?: boolean;
  engage?: boolean;
}

// --- Main Asset Type ---

export interface Asset {
  _id: string;
  accountId?: string;
  teamId?: string;

  // Type classification
  type?: AssetType;
  fileType?: FileType;
  appType?: string;
  webType?: string;
  youtubeType?: string;
  subType?: string;
  commonType?: string;
  serviceType?: string;

  // File info
  originalFileName?: string;
  originalFileSize?: number;
  originalFileExtension?: string;
  fileSize?: number;
  fileExtension?: string;
  filename?: string;

  // Storage
  AWSS3ID?: string;
  originalAWSS3ID?: string;
  thumbnail?: string;
  bucket?: string;
  /** Transloadit assembly id of the current file. Needed by Replace Asset to set
   *  processIdUpdateFile (the upload-processor webhook restores it on assembly failure). */
  processId?: string;

  // Path / Group
  path?: string;
  groupId?: string;
  /** Parent app id when this asset is a child of a drive app (Google Drive / OneDrive /
   *  Dropbox). Present ⟹ read-only in the detail view (cannot rename/tag/delete). */
  assetRootId?: string;

  // Content
  webLink?: string;
  embedLink?: string;
  /** The SOURCE length, always — never the trimmed playback window. The config form range-checks
   *  against it (`useSourceDuration` → `_assetDuration`) and open-api clamps against it
   *  (`clampYoutubeEndToDuration`). The window is derived where it is needed; see
   *  `playlists/utils/resolve-playlist-item-duration.ts resolvePlaybackWindow` (#2185). */
  duration?: number;
  /** YouTube playback window (#2105) — both measured from the START OF THE VIDEO, in the same
   *  frame. For an END, absent and 0 both mean "no end": no url carries an end mark, so there is
   *  no second writer to defer to. On `asset.model.ts` + `AssetInput`; which queries select them:
   *  grep `startSeconds` in packages/graphql-client. */
  startSeconds?: number;
  endSeconds?: number;
  video_bitrate?: number;
  video_codec?: string;
  width?: number;
  height?: number;
  num_pages?: number;
  doc_pages?: string[];
  /** Power BI multi-page container — total selected pages (`pages.length`, mirrored server-side). */
  pageCount?: number;
  /** Power BI kiosk mode (touch interaction) — Files-list badge; the REST row is what the screen reads. */
  interactiveMode?: boolean;
  /** Power BI multi-page container — the customer's chosen report pages, in play order. See `AssetPage`. */
  pages?: AssetPage[];

  // Status
  status?: AssetStatus;
  contentStatus?: string;
  isDisable?: boolean;
  isHide?: boolean;

  // Emergency (Flash Message / CAP Alert)
  emergencyDuration?: number;
  currentDate?: string;
  startDate?: string;

  // Tags
  tags?: string[];

  // Engagement
  engage?: boolean;

  // Display name
  name?: string;

  // Cloud storage (OneDrive, Google Drive, Dropbox) — playlist backing this drive asset
  playlistId?: string;

  // SmallApp text content
  text?: string;

  // SmallApp post type discriminator (e.g. POSTS_TYPES.EDITOR, POSTS_TYPES.GOOGLE_TRAFIC)
  postsType?: string;

  // Options & metadata
  options?: AssetOptions;
  meta?: Record<string, unknown>;
  refreshInterval?: number;
  /** Website smallapp "Load only first time (no refresh)" toggle — pairs with refreshInterval: 0. */
  noRefresh?: boolean;

  // Web/app advanced fields
  javascriptMaxRetries?: number;
  requestDesktopSite?: boolean;
  isPasswordMaster?: boolean;
  newWebView?: boolean;
  isNormalWebview?: boolean;
  webPageUrl?: string;
  javascriptRun?: string;
  appVersion?: string;
  /** Default per-page playback duration (seconds) for PDF/document assets. */
  durationPage?: number;
  /** Sparse map of 0-based page index (string key) → seconds, for PDF/document
   *  per-page playback timing. Pages absent here fall back to `durationPage`. */
  pageDurations?: Record<string, number>;
  displayIniFrame?: boolean;
  isAutoScroll?: boolean;
  scrollType?: string;
  scrollTo?: string;
  pageHeight?: number;
  zoom?: number;
  preload?: boolean;

  // Google Traffic fields
  location?: Record<string, unknown>;
  isShowMarker?: boolean;

  // Google Sheets / Google Slides fields
  sheetList?: unknown[];
  speed?: number;
  speedValue?: number;
  versionApp?: string;
  scaleSpct?: number;
  horizontalAlignment?: string;
  verticalAlignment?: string;
  repeatInterval?: number;
  profile?: Record<string, unknown>;
  folder?: string;
  sheetSelected?: unknown;
  margins?: unknown;
  isMargins?: boolean;

  // YouTube variants (Playlist / Channel / Live Schedule)
  isShuffle?: boolean;
  /** ISO datetime — scheduled live-stream start time */
  liveAt?: string;
  /** 'none' | 'daily' | 'weekly' — Live Schedule recurrence */
  repeat?: string;

  // Web Scripting (scriptingAbility) — manifest-driven
  /** Plaintext recording JSON — write-only (BE encrypts to recordingJson) */
  recording?: string;
  /** Plaintext extra script — write-only (BE encrypts to scriptInjectionJson) */
  scriptInjection?: string;
  /** 2FA secret (encrypted on persist) */
  secretTwoFA?: string;
  /** 2FA recovery code (plaintext) */
  recordTwoFA?: string;
  /** Delay (seconds) before executing main login script */
  javascriptDelayExecute?: number;
  /** Delay (seconds) before executing 2FA script */
  javascriptDelay2FAExecute?: number;
  /** Encryption format marker — 'v2' for current scriptingAbility encryption */
  versionJson?: string;

  // Expiration
  assetExpired?: AssetExpired;

  // Dates
  createdAt?: string;
  lastUpdatedDate?: string;
  createdBy?: string;
  lastUpdatedBy?: string;

  // Twitter source criteria — player reads postsType + socialProfile|hashtag to know what to fetch
  socialProfile?: string;
  hashtag?: string;

  // Extended info (returned by detail queries)
  parentGroupInfo?: AssetGroup;
  createdByInfo?: {
    fullName?: string;
    userEmail?: string;
  };
  lastUpdatedByInfo?: {
    fullName?: string;
    userEmail?: string;
  };
}

// --- Connection / Pagination Types ---

export interface AssetEdge {
  cursor?: string;
  node: Asset;
}

export interface AssetPageInfo {
  startCursor?: string;
  endCursor?: string;
  hasPreviousPage?: boolean;
  hasNextPage: boolean;
}

export interface AssetPageData {
  limit?: number;
  offset?: number;
  total?: number;
}

export interface AssetGroup extends BaseFolderSecurity {
  name?: string;
  type?: GroupType;
  description?: string;
  thumbnail?: string;
  bucket?: string;
  createdAt?: string;
  lastUpdatedDate?: string;
  /** Populated user details for display in share confirmation */
  securitiesDetail?: Array<{ name: string; userId: string; role?: string }>;
  /** Populated team details for display in share confirmation */
  securityTeamsDetail?: Array<{ name: string; _id: string }>;
  sharedLink?: boolean;
  /**
   * @deprecated v3 — replaced by per-entry `isEdit`. Kept readable during the dual-read
   * migration window (D9); stop writing it.
   */
  isFolderSecurityView?: boolean;
}

export interface AssetsResponse {
  page: {
    edges: AssetEdge[];
    pageInfo: AssetPageInfo;
  };
  pageData?: AssetPageData;
  extensions?: {
    groups?: AssetGroup[];
  };
}

// --- Input Types ---

export interface QueryAssetInput {
  _id?: string;
  _id_in?: string[];
  /** Exclude these ids — the "rest" section of the favorite-first split (client sends the fav id-set). */
  _id_nin?: string[];
  originalFileName?: string;
  originalFileName_contains?: string;
  originalFileName_startsWith?: string;
  originalFileName_regex?: string;
  isDisable?: boolean;
  type?: string;
  type_ne?: string;
  type_in?: string[];
  type_nin?: string[];
  appType?: string;
  appType_ne?: string | null;
  appType_in?: string[];
  appType_nin?: string[];
  subType?: string;
  subType_in?: string[];
  webType_in?: string[];
  webType_nin?: string[];
  commonType?: string;
  commonType_ne?: string | null;
  commonType_in?: string[];
  commonType_nin?: string[];
  fileType?: string;
  fileType_ne?: string;
  fileType_in?: string[];
  fileType_nin?: string[];
  originalFileExtension_nin?: string[];
  engage?: boolean;
  engage_ne?: boolean;
  engage_in?: boolean[];
  tags?: string;
  tags_in?: string[];
  path?: string | null;
  /** Path-prefix match (backend compiles to case-insensitive ^prefix regex — the
   *  same match updateObjectsTags' folder cascade uses, so counts = blast radius). */
  path_startsWith?: string;
  teamId?: string;
  /** When true, queries only assets visible via folder-level security sharing */
  isSharedWithMe?: boolean;
  /** Filter by root cloud storage asset ID (DRIVE mode — Google Drive, OneDrive, Dropbox) */
  assetRootId?: string;
  AND?: QueryAssetInput[];
  OR?: QueryAssetInput[];
}

export interface AssetSort {
  originalFileName?: SortDirection;
  createdAt?: SortDirection;
  lastUpdatedDate?: SortDirection;
}

export interface GroupSort {
  name?: SortDirection;
  createdAt?: SortDirection;
  lastUpdatedDate?: SortDirection;
}

export interface AssetInput {
  _id?: string;
  teamId?: string;
  type?: AssetType;
  fileType?: FileType;
  appType?: string;
  webType?: string;
  originalFileName?: string;
  originalFileSize?: number;
  path?: string;
  groupId?: string;
  tags?: string[];
  engage?: boolean;
  webLink?: string;
  thumbnail?: string;
  options?: AssetOptions;
  status?: AssetStatus;
  [key: string]: unknown;
}

export interface DeleteObjectInput {
  type?: string;
  typeGroup?: string;
  ids?: string[];
  groupIds?: string[];
  /** true = move to Trash (soft delete); false/absent = permanent delete (legacy behaviour). */
  isTrash?: boolean;
}

export interface GroupInput {
  _id?: string;
  type?: GroupType;
  name?: string;
  description?: string;
  path?: string;
  teamId?: string;
  thumbnail?: string;
  bucket?: string;
  /** v3 folder-security write shape — `userId` '-1' = "Only Admin". */
  securities?: Array<{ userId: string; isEdit: boolean }>;
  /** v3 folder-security write shape — `teamId` '-2' = "Everyone in the account". */
  securityFolderTeams?: Array<{ teamId: string; isEdit: boolean }>;
  /** v3 folder-security write shape — the security group(s) this folder is shared to. */
  securityGroupIds?: Array<{ groupId: string; isEdit: boolean }>;
  isHoldDevice?: boolean;
  /**
   * @deprecated v3 — replaced by per-entry `isEdit`. Kept readable during the dual-read
   * migration window (D9); stop writing it.
   */
  isFolderSecurityEdit?: boolean;
  /**
   * @deprecated v3 — replaced by per-entry `isEdit`. Kept readable during the dual-read
   * migration window (D9); stop writing it.
   */
  isFolderSecurityView?: boolean;
}

export interface MoveToGroupInput {
  type?: GroupType;
  newPath?: string;
  ids?: string[];
  groupIds?: string[];
  toTeamId?: string;
  fromTeamId?: string;
}

// --- Asset Picker Types ---

export const ASSET_PICKER_TYPES = {
  DEFAULT: 'default',
  PLAYLIST: 'playlist',
  SPLIT_SCREEN: 'split-screen',
  IMAGE: 'image',
  VIDEO: 'video',
  DOCUMENT: 'document',
  AUDIO: 'audio',
  APP: 'app',
  AI: 'ai',
  SENSOR: 'sensor',
  FOLDER: 'folder',
  DESIGNER: 'designer',
  DESIGNER_LINK: 'designer-link',
  DESIGNER_CREATOR: 'designer-creator',
  /** Broadcast (scio3#2033): assets a scheduled screenshot can actually capture. */
  BROADCAST: 'broadcast',
} as const;

export type AssetPickerType = (typeof ASSET_PICKER_TYPES)[keyof typeof ASSET_PICKER_TYPES];

/**
 * Client-side filter: returns true if the asset should be excluded from the picker.
 * Used to filter Atlas Search results which bypass server-side query filters.
 */
export function isAssetExcludedFromPicker(
  asset: { type?: string; fileType?: string; appType?: string; commonType?: string },
  pickerType: AssetPickerType = ASSET_PICKER_TYPES.DEFAULT,
): boolean {
  const filter = buildAssetPickerFilter(pickerType);
  const typeNin = filter.type_nin as string[] | undefined;
  const fileTypeNin = filter.fileType_nin as string[] | undefined;
  const appTypeNin = filter.appType_nin as string[] | undefined;
  const commonTypeNin = filter.commonType_nin as string[] | undefined;

  if (typeNin && asset.type && typeNin.includes(asset.type)) return true;
  if (fileTypeNin && asset.fileType && fileTypeNin.includes(asset.fileType)) return true;
  if (appTypeNin && asset.appType && appTypeNin.includes(asset.appType)) return true;
  if (commonTypeNin && asset.commonType && commonTypeNin.includes(asset.commonType)) return true;
  return false;
}

/** Build query filter for SelectAssetModal based on picker context.
 * NOTE: isAssetExcludedFromPicker() above derives its logic from this function.
 * If you change filter rules here, the client-side filter stays in sync automatically. */
export function buildAssetPickerFilter(
  pickerType: AssetPickerType = ASSET_PICKER_TYPES.DEFAULT,
): Partial<QueryAssetInput> {
  switch (pickerType) {
    // Broadcast: a scheduled screenshot of one asset, rendered once in headless Chromium.
    // What is excluded is NOT "apps we don't like" — it is content whose single frame is
    // worthless, or which has no frame at all. The engine decides *renderability* on its own
    // (self-rendering webLink/embedLink → else the small-app frontendUrl when the app needs no
    // signature → else the run fails), so this filter only has to encode the SEMANTIC cases.
    // Notably NOT excluded: BI dashboards. Power BI and Tableau assets render standalone from
    // their embed link — that is why an asset capture works where a screen capture did not.
    case ASSET_PICKER_TYPES.BROADCAST:
      return {
        type_nin: [
          // Composites: N zones, each possibly a playlist, a schedule or a video.
          ASSET_TYPES.SPLIT_SCREEN,
          ASSET_TYPES.FOLDER,
          ASSET_TYPES.DATASOURCE,
          ASSET_TYPES.TEMPLATE,
          ASSET_TYPES.HIDDEN,
        ],
        // No visual surface (audio) / no codec in the capture browser (video).
        fileType_nin: [FILE_TYPES.AUDIO, FILE_TYPES.VIDEO],
        // Video and stream web types live in `webType` with no appType, so neither type_nin nor
        // appType_nin reaches them. `youtube` covers its youtubeType variants (nasaTv, skyNews,
        // youtubeChannel/Playlist/LiveSchedule) since they are all webType 'youtube'.
        webType_nin: [
          WEB_TYPES.YOUTUBE,
          WEB_TYPES.YOUTUBE_LIVE,
          WEB_TYPES.VIDEO,
          WEB_TYPES.VIMEO,
          WEB_TYPES.STREAMING_APP,
        ],
        // Overlays that are not standalone screen content (qr, emergency, flash), plus Live TV
        // with Ads — a splitScreen composite identified by its commonType.
        commonType_nin: [...APP_COMMON_TYPES_REJECT_DEVICE, COMMON_TYPE.LIVE_TV_ADS],
        appType_nin: [...BROADCAST_REJECT_APP_TYPES],
      };
    case ASSET_PICKER_TYPES.DEFAULT:
      return {
        type_nin: [
          COMMON_TYPE.QR,
          APP_TYPES.EMERGENCY_MESSAGE,
          APP_TYPES.FLASH_MESSAGE,
          APP_TYPES.EMERGENCY_FEED,
          ASSET_TYPES.TEMPLATE,
          ASSET_TYPES.DATASOURCE,
        ],
        fileType_nin: [FILE_TYPES.AUDIO],
        commonType_nin: [...APP_COMMON_TYPES_REJECT_DEVICE],
        appType_nin: [...APP_REJECT_ASSIGN],
      };
    case ASSET_PICKER_TYPES.PLAYLIST:
      return {
        type_nin: [
          COMMON_TYPE.QR,
          APP_TYPES.EMERGENCY_MESSAGE,
          APP_TYPES.FLASH_MESSAGE,
          APP_TYPES.EMERGENCY_FEED,
          APP_TYPES.BACKGROUND_MUSIC,
          ASSET_TYPES.TEMPLATE,
          ASSET_TYPES.DATASOURCE,
          ASSET_TYPES.FOLDER,
        ],
        fileType_nin: [FILE_TYPES.AUDIO],
        appType_nin: [...APP_REJECT_ASSIGN, ...PLAYLIST_IGNORE_APP_TYPES],
        commonType_nin: [...APP_COMMON_TYPES_REJECT_DEVICE, COMMON_TYPE.LIVE_TV_ADS],
      };
    // Split-screen zone content picker — mirrors scio1
    // `splitScreenZonesModal.openPicker` (zonesModal.js:1128-1134):
    //   type ∉ {APP_COMMON_TYPES, splitScreen, template, dataSource}
    //   fileType ∉ {audio}
    //   commonType ∉ APP_COMMON_TYPES_REJECT_SPLITSCREEN
    //   appType ∉ APP_REJECT_ASSIGN
    case ASSET_PICKER_TYPES.SPLIT_SCREEN:
      return {
        type_nin: [ASSET_TYPES.SPLIT_SCREEN, ASSET_TYPES.TEMPLATE, ASSET_TYPES.DATASOURCE],
        fileType_nin: [FILE_TYPES.AUDIO],
        commonType_nin: [...APP_COMMON_TYPES_REJECT_SPLITSCREEN],
        appType_nin: [...APP_REJECT_ASSIGN],
      };
    case ASSET_PICKER_TYPES.IMAGE:
      return {
        type: ASSET_TYPES.FILE,
        fileType: FILE_TYPES.IMAGE,
        commonType_nin: [COMMON_TYPE.QR],
      };
    case ASSET_PICKER_TYPES.VIDEO:
      return {
        type: ASSET_TYPES.FILE,
        fileType: FILE_TYPES.VIDEO,
        commonType_nin: [COMMON_TYPE.QR],
      };
    case ASSET_PICKER_TYPES.DOCUMENT:
      return {
        type: ASSET_TYPES.FILE,
        fileType: FILE_TYPES.DOCUMENT,
        commonType_nin: [COMMON_TYPE.QR],
      };
    case ASSET_PICKER_TYPES.AUDIO:
      return {
        type: ASSET_TYPES.FILE,
        fileType: FILE_TYPES.AUDIO,
        commonType_nin: [COMMON_TYPE.QR],
      };
    case ASSET_PICKER_TYPES.APP:
      return {
        fileType: FILE_TYPES.WEB,
        commonType_nin: [COMMON_TYPE.QR],
      };
    case ASSET_PICKER_TYPES.AI:
      return {
        appType: APP_TYPES.AI,
      };
    case ASSET_PICKER_TYPES.SENSOR:
      return {
        appType: APP_TYPES.SENSOR,
      };
    case ASSET_PICKER_TYPES.FOLDER:
      return {};
    case ASSET_PICKER_TYPES.DESIGNER:
      // Backend handles filtering via extendParams.isFilterDesigner (appMeta tags: 'designer')
      return {};
    case ASSET_PICKER_TYPES.DESIGNER_LINK:
      // Backend handles filtering via extendParams.isFilterDesignerLink (appMeta tags: 'designer-link')
      return {};
    case ASSET_PICKER_TYPES.DESIGNER_CREATOR:
      // Opens Designer editor directly — no asset picker filter needed
      return {};
    default:
      return {};
  }
}

/**
 * Picker types whose membership the BACKEND resolves, not the query filter.
 *
 * For these, open-api reads `extendParams.isFilterDesigner` / `isFilterDesignerLink`, builds an
 * OR over the appMeta tag set (`_buildAppMetaTagOr`) and — critically — DELETES any top-level
 * `fileType` / `type` filter before applying it (`updateIdFilterByAppMetaTag`). That is why both
 * cases in `buildAssetPickerFilter` return `{}`.
 *
 * A caller-supplied `type` / `fileType` filter on one of these therefore cannot narrow the
 * picker; it can only make the client disagree with the server, which shows up as assets
 * silently missing.
 */
export const BACKEND_RESOLVED_PICKER_TYPES: readonly AssetPickerType[] = [
  ASSET_PICKER_TYPES.DESIGNER,
  ASSET_PICKER_TYPES.DESIGNER_LINK,
];

/**
 * The exact two fields open-api drops for a backend-resolved picker
 * (`asset.service.ts` `updateIdFilterByAppMetaTag`: `delete filters.fileType; delete filters.type`).
 *
 * Every operator form goes with them, not just the positive ones: `fillMongoFiltersObject`
 * collapses `fileType`, `fileType_in`, `fileType_nin` and `fileType_ne` into the single
 * `filters.fileType` slot, so deleting that slot server-side discards all four.
 *
 * `appType` and `commonType` are deliberately NOT here. The server keeps those and ANDs them
 * with its OR, so a caller narrowing by app type is honoured — dropping them client-side would
 * make SEARCH (filtered client-side) show more than BROWSE (filtered server-side).
 */
const BACKEND_OWNED_FILTER_FIELDS = ['type', 'fileType'] as const;

/**
 * The filter a picker actually applies: its pickerType defaults, overridden by the caller's
 * `queryFilter`. Single source of truth for `SelectAssetModal`'s `mergedFilter` — the same
 * object feeds the server query, the browse-cache filter and the search-result filter, so all
 * three stay in agreement.
 *
 * Two rules beyond a plain spread:
 *
 * 1. When the caller sets a BASE field (a key with no `_`), the pickerType's operator variants
 *    for that field (`_nin` / `_ne` / `_in`) are dropped, so the backend never receives
 *    `fileType: 'document'` and `fileType.$nin` in the same query.
 * 2. For a backend-resolved pickerType, a caller's `type` / `fileType` restriction is dropped,
 *    mirroring exactly what the server drops — see BACKEND_RESOLVED_PICKER_TYPES and
 *    BACKEND_OWNED_FILTER_FIELDS. The Designer's "Add Link" picker shipped this mistake: it
 *    described "image/video/document files OR a designer-link app" as a flat `fileType_in`,
 *    which an app asset (no `fileType`) can never satisfy, and the picker lost every app.
 *    Nothing else the caller passes is touched, and the user's own sidebar fileType dropdown is
 *    applied later (`applyFileTypeFilter`), so it still works.
 */
export function buildMergedAssetPickerFilter(
  pickerType?: AssetPickerType,
  queryFilter?: Partial<QueryAssetInput>,
): Partial<QueryAssetInput> {
  const merged: Partial<QueryAssetInput> = {
    ...(pickerType ? buildAssetPickerFilter(pickerType) : {}),
    ...queryFilter,
  };
  if (!queryFilter) return merged;

  const bag = merged as Record<string, unknown>;
  for (const key of Object.keys(queryFilter)) {
    if (!key.includes('_')) {
      delete bag[`${key}_nin`];
      delete bag[`${key}_ne`];
      delete bag[`${key}_in`];
    }
  }
  if (pickerType && BACKEND_RESOLVED_PICKER_TYPES.includes(pickerType)) {
    for (const field of BACKEND_OWNED_FILTER_FIELDS) {
      for (const key of Object.keys(queryFilter)) {
        if (key === field || key.startsWith(`${field}_`)) delete bag[key];
      }
    }
  }
  return merged;
}

/**
 * Apply a user-selected fileType filter on top of a base query, removing
 * any conflicting `fileType_nin` so the backend doesn't receive both
 * `fileType: "document"` and `fileType.$nin` in the same query.
 */
export function applyFileTypeFilter<T extends Partial<QueryAssetInput>>(
  query: T,
  fileType: string | null,
): T {
  if (fileType) {
    query.fileType = fileType as T['fileType'];
    delete query.fileType_nin;
  }
  return query;
}
